/**
 * Piesele pe care le folosesc mai multe blocuri: titlul unei sectiuni si
 * butonul care duce undeva.
 *
 * Scrise o singura data pentru ca altfel ar fi aparut in fiecare bloc, usor
 * diferit — iar diferentele mici pe zece sectiuni ale aceleiasi pagini se citesc
 * ca dezordine, nu ca varietate.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { resolveHref } from "./context";

export function BlockHeading({
  title,
  subtitle,
  level,
  align = "stanga",
  id,
}: {
  title: string;
  subtitle?: string;
  level: 1 | 2;
  align?: string;
  id?: string;
}) {
  if (title.length === 0 && (subtitle === undefined || subtitle.length === 0)) return null;
  const alignClass = align === "centru" ? "text-center items-center" : "";
  return (
    <div className={`flex flex-col gap-1.5 ${alignClass}`}>
      {title.length === 0 ? null : level === 1 ? (
        <h1 id={id} className="text-2xl font-bold text-ink sm:text-3xl">
          {title}
        </h1>
      ) : (
        <h2 id={id} className="text-xl font-bold text-ink sm:text-2xl">
          {title}
        </h2>
      )}
      {subtitle === undefined || subtitle.length === 0 ? null : (
        <p className="max-w-readable text-base text-ink-muted">{subtitle}</p>
      )}
    </div>
  );
}

const LINK_VARIANTS: Record<string, string> = {
  primary: "bg-brand text-brand-contrast hover:bg-brand-hover",
  secondary: "bg-surface text-ink border border-line hover:bg-surface-hover",
  contrast: "bg-white text-black hover:opacity-90",
};

/**
 * Butonul unui bloc. Intoarce `null` cand legatura lipseste sau nu e acceptata:
 * un buton fara destinatie cere o apasare si nu raspunde, ceea ce e mai rau
 * decat lipsa lui.
 */
export function BlockLink({
  label,
  href,
  variant = "primary",
  children,
}: {
  label: string;
  href: string;
  variant?: keyof typeof LINK_VARIANTS | string;
  children?: ReactNode;
}) {
  const text = label.trim();
  if (text.length === 0) return null;
  const target = resolveHref(href);
  if (target === null) return null;

  const className = [
    "da-tap inline-flex items-center justify-center gap-2 rounded-control px-5 py-2.5 text-base font-semibold",
    LINK_VARIANTS[variant] ?? LINK_VARIANTS.primary,
  ].join(" ");

  if (target.kind === "intern") {
    return (
      <Link to={target.to} className={className}>
        {children}
        {text}
      </Link>
    );
  }
  return (
    // `noreferrer` pe langa `noopener`: adresa e scrisa de operatorul pietei, dar
    // poate duce oriunde, iar antetul de referinta ar dezvalui pagina de pe care
    // a plecat clientul.
    <a href={target.href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
      {text}
    </a>
  );
}

/**
 * Invelisul comun al unei sectiuni de pagina.
 *
 * `id` e identitatea blocului si, implicit, ANCORA lui. Fara ea, o legatura
 * scrisa in editor ca `#vitrine` nu avea unde sa ducă — iar butonul principal al
 * unei coperte de sablon nu facea nimic la apasare.
 */
export function BlockSection({
  children,
  id,
  labelledBy,
  className = "",
}: {
  children: ReactNode;
  id?: string;
  labelledBy?: string;
  className?: string;
}) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      // Ancora sare SUB antetul lipit, nu in spatele lui: inaltimea reala a
      // antetului e publicata in `--da-header-h` de `Layout`.
      style={{ scrollMarginTop: "calc(var(--da-header-h) + 1rem)" }}
      className={`flex flex-col gap-4 ${className}`}
    >
      {children}
    </section>
  );
}
